import React, { useCallback, useEffect, useRef, useState } from "react";
import { Check, Copy, Loader2, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

const ACCEPT = "image/jpeg,image/png,image/webp,image/gif";
const MAX_MB = 15;

async function fetchList() {
    const res = await fetch(`${API}/assets`, { credentials: "include" });
    if (!res.ok) throw new Error("Failed to load assets");
    return res.json();
}

async function uploadOne(file) {
    if (file.size > MAX_MB * 1024 * 1024) {
        throw new Error(`${file.name} is over ${MAX_MB} MB`);
    }
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`${API}/assets`, {
        method: "POST",
        credentials: "include",
        body: fd,
    });
    if (!res.ok) {
        let detail;
        try {
            detail = (await res.json()).detail;
        } catch {
            detail = res.statusText;
        }
        throw new Error(detail || "Upload failed");
    }
    return res.json();
}

async function deleteOne(id) {
    const res = await fetch(`${API}/assets/${id}`, {
        method: "DELETE",
        credentials: "include",
    });
    if (!res.ok) throw new Error("Delete failed");
}

export default function AssetsDialog({ open, onOpenChange, onChange }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [copiedId, setCopiedId] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const inputRef = useRef(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const list = await fetchList();
            setItems(list);
            onChange?.(list.length);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setLoading(false);
        }
    }, [onChange]);

    useEffect(() => {
        if (open) refresh();
    }, [open, refresh]);

    const handleFiles = async (files) => {
        const arr = Array.from(files || []);
        if (!arr.length) return;
        setUploading(true);
        try {
            const results = await Promise.allSettled(arr.map(uploadOne));
            const ok = results.filter((r) => r.status === "fulfilled").length;
            const failed = results.filter((r) => r.status === "rejected");
            failed.forEach((r) => toast.error(r.reason.message));
            if (ok) toast.success(`${ok} image${ok > 1 ? "s" : ""} uploaded`);
            await refresh();
        } finally {
            setUploading(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleFiles(e.dataTransfer.files);
    };

    const copyUrl = async (item) => {
        try {
            await navigator.clipboard.writeText(item.url);
            setCopiedId(item.asset_id);
            setTimeout(() => setCopiedId(null), 1500);
        } catch {
            toast.error("Copy failed");
        }
    };

    const remove = async (item) => {
        try {
            await deleteOne(item.asset_id);
            toast.success("Deleted");
            await refresh();
        } catch (e) {
            toast.error(e.message);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="assets-dialog"
                className="rounded-none border-foreground max-w-4xl h-[80vh] flex flex-col p-0 gap-0"
            >
                <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
                    <DialogTitle className="tracking-tight text-2xl font-black">
                        Asset library
                    </DialogTitle>
                    <DialogDescription>
                        Images you upload here are made available to the AI in the next generation — it will reference them by URL when they fit the brief.
                    </DialogDescription>
                </DialogHeader>

                {/* Upload area */}
                <div className="p-6 border-b border-border">
                    <div
                        data-testid="asset-dropzone"
                        onDragOver={(e) => {
                            e.preventDefault();
                            setDragOver(true);
                        }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={onDrop}
                        onClick={() => inputRef.current?.click()}
                        className={`border-2 border-dashed cursor-pointer p-8 grid place-items-center text-center transition-colors ${
                            dragOver ? "border-foreground bg-secondary" : "border-border hover:bg-secondary"
                        }`}
                    >
                        <input
                            ref={inputRef}
                            data-testid="asset-file-input"
                            type="file"
                            accept={ACCEPT}
                            multiple
                            className="hidden"
                            onChange={(e) => {
                                handleFiles(e.target.files);
                                e.target.value = "";
                            }}
                        />
                        {uploading ? (
                            <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em]">
                                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                                Uploading…
                            </div>
                        ) : (
                            <div>
                                <Upload className="w-6 h-6 mx-auto" strokeWidth={1.5} />
                                <div className="mt-3 font-black text-base tracking-tight">
                                    Drop images here, or click to browse
                                </div>
                                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                    ▍ jpeg · png · webp · gif · up to {MAX_MB} MB each
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Grid */}
                <div className="flex-1 overflow-y-auto loom-scroll p-6">
                    {loading ? (
                        <div className="overline">▍ Loading library…</div>
                    ) : items.length === 0 ? (
                        <div className="border border-dashed border-border p-12 text-center">
                            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                                ▍ No images yet — upload one to start
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                            {items.map((item) => (
                                <div
                                    key={item.asset_id}
                                    data-testid={`asset-card-${item.asset_id}`}
                                    className="group border border-border bg-secondary"
                                >
                                    <div className="aspect-square bg-white overflow-hidden">
                                        <img
                                            src={item.url}
                                            alt={item.filename}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="p-2 border-t border-border">
                                        <div
                                            className="font-mono text-[10px] uppercase tracking-[0.16em] truncate text-muted-foreground"
                                            title={item.filename}
                                        >
                                            {item.filename}
                                        </div>
                                        <div className="mt-2 flex items-center gap-1">
                                            <button
                                                data-testid={`copy-asset-${item.asset_id}`}
                                                onClick={() => copyUrl(item)}
                                                className="flex-1 inline-flex items-center justify-center gap-1 h-7 border border-foreground font-mono text-[9px] uppercase tracking-[0.16em] hover:bg-foreground hover:text-background transition-colors"
                                            >
                                                {copiedId === item.asset_id ? (
                                                    <>
                                                        <Check className="w-3 h-3" strokeWidth={1.5} /> Copied
                                                    </>
                                                ) : (
                                                    <>
                                                        <Copy className="w-3 h-3" strokeWidth={1.5} /> Copy URL
                                                    </>
                                                )}
                                            </button>
                                            <button
                                                data-testid={`delete-asset-${item.asset_id}`}
                                                onClick={() => remove(item)}
                                                className="w-7 h-7 grid place-items-center border border-border text-muted-foreground hover:text-accent hover:border-foreground transition-colors"
                                                title="Delete"
                                            >
                                                <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
